import type { Injector, Signal } from '@angular/core';
import type { Params } from '@angular/router';

import { assertInjector } from 'ngxtension/assert-injector';
import { injectQueryParams } from 'ngxtension/inject-query-params';

/** Query params parsed into a multi-value map, e.g. `?a=1,2` -> `{ a: ['1', '2'] }`. */
export type QueryParamsMap = Record<string, string[]>;

/**
 * Query-param prefixes that are excluded from the map by default
 * (tracking and Storyblok editor params).
 */
export const DEFAULT_EXCLUDED_PARAM_PREFIXES: readonly string[] = [
	'_gl',
	'_storyblok',
	'dclid',
	'fbclid',
	'gad_',
	'gbraid',
	'gclid',
	'gclsrc',
	'li_fat_id',
	'msclkid',
	'rdt_cid',
	'scclid',
	'srsltid',
	'ttclid',
	'twclid',
	'utm_',
	'wbraid',
];

export interface QueryParamsMapOptions {
	excludedPrefixes?: readonly string[];
	injector?: Injector;
}

/**
 * Parses route query params into a multi-value map. Values are
 * comma-separated (`?facilities=a,b`); repeated keys (`?a=1&a=2`) are
 * flattened in URL order. Empty segments, duplicate values (first occurrence
 * wins) and keys without remaining values are dropped, as are keys matching
 * an excluded prefix.
 *
 * Key and value order is preserved exactly as it appears in the URL — no
 * sorting or casing changes — so the server and client derive an identical
 * map from the same URL (required for `TransferState` cache-key determinism).
 */
export function toQueryParamsMap(
	params: Params,
	excludedPrefixes: readonly string[] = DEFAULT_EXCLUDED_PARAM_PREFIXES,
): QueryParamsMap {
	const map: QueryParamsMap = {};

	for (const [key, value] of Object.entries(params)) {
		if (excludedPrefixes.some((prefix) => key.startsWith(prefix))) {
			continue;
		}

		const raw: unknown[] = Array.isArray(value) ? value : [value];
		const values = raw
			.flatMap((entry) =>
				typeof entry === 'string' ? entry.split(',') : [],
			)
			.map((entry) => entry.trim())
			.filter(Boolean);
		const uniqueValues = [...new Set(values)];

		if (uniqueValues.length) {
			map[key] = uniqueValues;
		}
	}

	return map;
}

/**
 * Signal of the current URL's query params as a multi-value map
 * (see {@link toQueryParamsMap}).
 */
export function injectQueryParamsMap(
	options: QueryParamsMapOptions = {},
): Signal<QueryParamsMap> {
	const { excludedPrefixes, injector } = options;

	return assertInjector(injectQueryParamsMap, injector, () =>
		injectQueryParams((params) =>
			toQueryParamsMap(params, excludedPrefixes),
		),
	);
}

/**
 * Deep equality for query-param maps: same keys and element-wise equal value
 * arrays (order-sensitive). Key order is irrelevant.
 */
export function queryParamsMapEqual(
	a: QueryParamsMap,
	b: QueryParamsMap,
): boolean {
	const aKeys = Object.keys(a);

	if (aKeys.length !== Object.keys(b).length) {
		return false;
	}

	return aKeys.every(
		(key) =>
			!!b[key] &&
			a[key].length === b[key].length &&
			a[key].every((value, index) => value === b[key][index]),
	);
}
